"use client";

import TFLink from "@/Web/Components/TFLink/TFLink";
import TFHorizontalSlider from "@/Web/Components/TFHorizontalSlider/TFHorizontalSlider";
import TFBadge from "@/Web/Components/TFBadge/TFBadge";
import TFLayoutHomeSection from "@/Web/Components/TFLayoutHomeSection/TFLayoutHomeSection";
import { liveRoomsData } from "./dataLiveRooms";
import { useState, useEffect } from "react";
import { IoMicSharp, IoPeopleSharp, IoPersonSharp } from "react-icons/io5";
import { useDirection } from "@/utils/useDirection";
import { useTranslations } from "next-intl";

const formatListeners = (count: number): string => {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k`;
  }
  return count.toString();
};

const formatDuration = (startedAt: string): string => {
  const start = new Date(startedAt);
  const now = new Date();
  const diffMinutes = Math.floor(
    (now.getTime() - start.getTime()) / (1000 * 60)
  );

  if (diffMinutes < 60) {
    return `${diffMinutes} мин`;
  }
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;
  return `${hours}ч ${minutes}м`;
};

const LiveRoomCard = ({
  room,
  href,
}: {
  room: {
    title: string;
    host: string;
    listeners: number;
    topic: string;
    startedAt: string;
    language: string;
  };
  href: string;
}) => {
  const t = useTranslations("web.pages.home.sections.liveRooms");
  const direction = useDirection();
  const isRTL = direction === "rtl";

  return (
    <TFLink
      href={href}
      className="bg-black/5 rounded-lg hover:bg-black/10 transition-colors h-full flex flex-col group"
    >
      {/* Placeholder для live комнаты */}
      <div className="relative w-full aspect-[4/3] rounded-t-lg bg-gradient-to-br from-red-500/10 to-red-600/20 flex items-center justify-center">
        <IoMicSharp className="w-16 h-16 text-red-500/40 group-hover:text-red-500/50 transition-colors translate-y-2" />
        <TFBadge
          title={room.topic}
          style="gray"
          className={`absolute top-2 ${isRTL ? "right-2" : "left-2"}`}
        />
        <div
          className={`absolute top-2 ${isRTL ? "left-2" : "right-2"} bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold flex items-center gap-1`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
          {t("onAir")}
        </div>
      </div>

      <div className="p-3 flex flex-col flex-grow">
        <h3 className="text-base font-semibold text-black line-clamp-2 group-hover:text-black/70 transition-colors mb-2">
          {room.title}
        </h3>

        <div className="text-sm text-black/60 mb-3">{room.host}</div>

        <div className="flex items-center gap-2 text-xs text-black/50 mt-auto">
          <div className="flex items-center gap-1">
            <IoPeopleSharp className="w-3 h-3 text-black/40" />
            <span>
              {formatListeners(room.listeners)} {t("listening")}
            </span>
          </div>
        </div>
      </div>
    </TFLink>
  );
};

export default function SectionLiveRooms() {
  const t = useTranslations("web.pages.home.sections.liveRooms");
  const [itemsPerView, setItemsPerView] = useState(4);

  useEffect(() => {
    const updateItemsPerView = () => {
      setItemsPerView(window.innerWidth < 768 ? 1 : 4);
    };

    updateItemsPerView();
    window.addEventListener("resize", updateItemsPerView);
    return () => window.removeEventListener("resize", updateItemsPerView);
  }, []);

  if (liveRoomsData.length === 0) {
    return null;
  }

  return (
    <TFLayoutHomeSection
      moreButton={{
        title: t("allRooms"),
        href: "/live",
      }}
      sideInfo={{
        title: t("title"),
        description: t("description"),
        statistics: [
          { title: t("onAir"), value: liveRoomsData.length.toString() },
          {
            title: t("listeners"),
            value: liveRoomsData
              .reduce((sum, room) => sum + room.listeners, 0)
              .toString(),
          },
        ],
        position: "right",
      }}
    >
      <TFHorizontalSlider
        items={liveRoomsData}
        renderItem={(room) => (
          <LiveRoomCard room={room} href={`/live/${room.id}`} />
        )}
        itemWidth={280}
        gap={16}
        autoScroll={false}
        showControls={true}
        itemsPerView={itemsPerView}
        showDots={true}
      />
    </TFLayoutHomeSection>
  );
}
